import capitalize from 'lodash/capitalize';

import { formatter as currencyFormatter } from '@zoopla/shared/currency';

const RADIUS_VALUES = [
    '0',
    '0.25',
    '0.5',
    '1',
    '3',
    '5',
    '10',
    '15',
    '20',
    '30',
    '40'
];

const MIN_MAX_BEDROOMS_VALUES = [
    '',
    '0',
    '1',
    '2',
    '3',
    '4',
    '5',
    '6',
    '7',
    '8',
    '9',
    '10'
];

const PROPERTY_TYPE_VALUES = ['', 'houses', 'flats', 'farms_land'];

const MIN_MAX_PRICE_VALUES = [
    '0',
    '10000',
    '20000',
    '30000',
    '40000',
    '50000',
    '60000',
    '70000',
    '80000',
    '90000',
    '100000',
    '110000',
    '120000',
    '125000',
    '130000',
    '140000',
    '150000',
    '160000',
    '170000',
    '180000',
    '190000',
    '200000',
    '210000',
    '220000',
    '230000',
    '240000',
    '250000',
    '275000',
    '300000',
    '325000',
    '350000',
    '375000',
    '400000',
    '425000',
    '450000',
    '475000',
    '500000',
    '550000',
    '600000',
    '650000',
    '700000',
    '750000',
    '800000',
    '850000',
    '900000',
    '950000',
    '1000000',
    '1100000',
    '1200000',
    '1300000',
    '1400000',
    '1500000',
    '1600000',
    '1700000',
    '1800000',
    '1900000',
    '2000000',
    '2100000',
    '2200000',
    '2300000',
    '2400000',
    '2500000',
    '2750000',
    '3000000',
    '3250000',
    '3500000',
    '3750000',
    '4000000',
    '4250000',
    '4500000',
    '4750000',
    '5000000',
    '5500000',
    '6000000',
    '6500000',
    '7000000',
    '7500000',
    '8000000',
    '8500000',
    '9000000',
    '9500000',
    '10000000',
    '12500000',
    '15000000'
];

const MIN_MAX_PRICE_WEEKLY_VALUES = [
    '0',
    '25',
    '50',
    '75',
    '100',
    '125',
    '150',
    '175',
    '200',
    '225',
    '250',
    '275',
    '300',
    '325',
    '350',
    '375',
    '400',
    '425',
    '450',
    '475',
    '500',
    '550',
    '600',
    '650',
    '700',
    '750',
    '800',
    '850',
    '900',
    '950',
    '1000',
    '1250',
    '1500',
    '1750',
    '2000',
    '2500',
    '3000',
    '3500',
    '4000',
    '4500',
    '5000',
    '7500'
];

const MIN_MAX_PRICE_MONTHLY_VALUES = [
    '0',
    '100',
    '200',
    '300',
    '400',
    '500',
    '600',
    '700',
    '800',
    '900',
    '1000',
    '1250',
    '1500',
    '1750',
    '2000',
    '2250',
    '2500',
    '2750',
    '3000',
    '3250',
    '3500',
    '3750',
    '4000',
    '4250',
    '4500',
    '4750',
    '5000',
    '5500',
    '6000',
    '6500',
    '7000',
    '7500',
    '8000',
    '8500',
    '9000',
    '9500',
    '10000',
    '12500',
    '15000',
    '17500',
    '20000',
    '25000'
];

export enum PriceFrequency {
    PerMonth = 'per_month',
    PerWeek = 'per_week'
}
const PRICE_FREQUENCY_VALUES = [
    PriceFrequency.PerMonth,
    PriceFrequency.PerWeek
];

const PROPERTY_SUB_TYPE_VALUES = [
    'detached',
    'semi_detached',
    'terraced',
    'bungalow',
    'detached_bungalow',
    'semi_detached_bungalow',
    'terraced_bungalow',
    'cottage',
    'park_home'
];

const PROPERTY_ADDED_VALUES = [
    '',
    '24_hours',
    '3_days',
    '7_days',
    '14_days',
    '30_days'
];

const AVAILABLE_FROM_VALUES = [
    '',
    'now',
    '1months',
    '3months',
    '6months',
    '12months'
];

const FURNISHED_STATE_VALUES = [
    'Any',
    'furnished',
    'part_furnished',
    'unfurnished'
];

const MUST_HAVES_VALUES = [
    'has_garden',
    'has_parking_garage',
    'has_balcony_terrace',
    'has_porter_security',
    'has_wood_floors',
    'has_fireplace',
    'is_rural_secluded'
];

const DURATION_VALUES = ['900', '1800', '2700', '3600', '4500'];

const TRANSPORT_TYPE_VALUES = [
    'driving',
    'walking',
    'walking_train',
    'public_transport',
    'cycling'
];

const radiusOptions: { [key: string]: string } = {
    '0': 'This area only',
    '0-25': '+ ¼ mile',
    '0-5': '+ ½ mile',
    '1': '+ 1 mile',
    '3': '+ 3 miles',
    '5': '+ 5 miles',
    '10': '+ 10 miles',
    '15': '+ 15 miles',
    '20': '+ 20 miles',
    '30': '+ 30 miles',
    '40': '+ 40 miles'
};

const bedsOptions: { [key: string]: string } = {
    '0': 'Studio',
    '1': '1',
    '2': '2',
    '3': '3',
    '4': '4',
    '5': '5',
    '6': '6',
    '7': '7',
    '8': '8',
    '9': '9',
    '10': '10+'
};

const propertyOptions: { [key: string]: string } = {
    '': 'Show all',
    all: 'All property types',
    houses: 'Houses',
    farms_land: 'Farms/land',
    flats: 'Flats'
};

const propertySubTypes: { [key: string]: string } = {
    bungalow: 'Bungalow',
    detached_bungalow: 'Detached bungalow',
    terraced_bungalow: 'Terraced bungalow',
    semi_detached_bungalow: 'Semi-detached bungalow',
    cottage: 'Cottage',
    detached: 'Detached house',
    semi_detached: 'Semi-detached house',
    studios: 'Studio',
    terraced: 'Terraced house',
    park_home: 'Mobile/park home'
};

const propertyAdded: { [key: string]: string } = {
    '': 'Added anytime',
    '24_hours': 'Last 24 hours',
    '3_days': 'Last 3 days',
    '7_days': 'Last 7 days',
    '14_days': 'Last 14 days',
    '30_days': 'Last 30 days'
};

const availableFrom: { [key: string]: string } = {
    '': 'Available anytime',
    now: 'Immediately',
    '1months': 'Within 1 month',
    '3months': 'Within 3 months',
    '6months': 'Within 6 months',
    '12months': 'Within 1 year'
};

const furnishedState: { [key: string]: string } = {
    Any: 'Any',
    furnished: 'Furnished',
    part_furnished: 'Part-furnished',
    unfurnished: 'Unfurnished'
};

const featureOptions: { [key: string]: string } = {
    has_garden: 'Garden',
    has_parking_garage: 'Parking / garage',
    has_balcony_terrace: 'Balcony / terrace',
    has_porter_security: 'Porter / security',
    has_wood_floors: 'Wood floors',
    has_fireplace: 'Fireplace',
    is_rural_secluded: 'Rural / secluded'
};

const durationOptions: { [key: string]: string } = {
    '900': '15 minutes',
    '1800': '30 minutes',
    '2700': '45 minutes',
    '3600': '1 hour',
    '4500': '1 ¼ hour'
};

const transportTypes: { [key: string]: string } = {
    driving: 'Driving',
    walking: 'Walking',
    walking_train: 'Train',
    public_transport: 'Public transport',
    cycling: 'Cycling'
};

export const filtersOptions = {
    radius: {
        options: RADIUS_VALUES.map((value) => {
            const tValue = value.replace('.', '-');

            return {
                label: radiusOptions[tValue],
                value
            };
        }),
        selectedValue: RADIUS_VALUES[0]
    },
    minBedrooms: {
        options: MIN_MAX_BEDROOMS_VALUES.map((value) => {
            const tValue = value.replace('.', '-');
            const label = bedsOptions[tValue] ?? 'No min';

            return {
                label,
                value
            };
        }),
        selectedValue: MIN_MAX_BEDROOMS_VALUES[0]
    },
    maxBedrooms: {
        options: MIN_MAX_BEDROOMS_VALUES.map((value) => {
            const tValue = value.replace('.', '-');
            const label = bedsOptions[tValue] ?? 'No max';

            return {
                label,
                value
            };
        }),
        selectedValue: MIN_MAX_BEDROOMS_VALUES[0]
    },
    propertyType: {
        options: PROPERTY_TYPE_VALUES.map((value) => {
            return {
                label: propertyOptions[value],
                value
            };
        }),
        selectedValue: PROPERTY_TYPE_VALUES[0]
    },
    minPrice: {
        options: MIN_MAX_PRICE_VALUES.map((value) => {
            const label =
                value === '0'
                    ? 'No min'
                    : `${currencyFormatter.format(parseInt(value))}`;

            return {
                label,
                value
            };
        }),
        selectedValue: MIN_MAX_PRICE_VALUES[0]
    },
    maxPrice: {
        options: MIN_MAX_PRICE_VALUES.map((value) => {
            const label =
                value === '0'
                    ? 'No max'
                    : `${currencyFormatter.format(parseInt(value))}`;

            return {
                label,
                value
            };
        }),
        selectedValue: MIN_MAX_PRICE_VALUES[0]
    },
    minPriceWeekly: {
        options: MIN_MAX_PRICE_WEEKLY_VALUES.map((value) => {
            const label =
                value === '0'
                    ? 'No min'
                    : `${currencyFormatter.format(parseInt(value, 10))} pw`;

            return {
                label,
                value
            };
        }),
        selectedValue: MIN_MAX_PRICE_WEEKLY_VALUES[0]
    },
    maxPriceWeekly: {
        options: MIN_MAX_PRICE_WEEKLY_VALUES.map((value) => {
            const label =
                value === '0'
                    ? 'No max'
                    : `${currencyFormatter.format(parseInt(value, 10))} pw`;

            return {
                label,
                value
            };
        }),
        selectedValue: MIN_MAX_PRICE_WEEKLY_VALUES[0]
    },
    minPriceMonthly: {
        options: MIN_MAX_PRICE_MONTHLY_VALUES.map((value) => {
            const label =
                value === '0'
                    ? 'No min'
                    : `${currencyFormatter.format(parseInt(value, 10))} pcm`;

            return {
                label,
                value
            };
        }),
        selectedValue: MIN_MAX_PRICE_MONTHLY_VALUES[0]
    },
    maxPriceMonthly: {
        options: MIN_MAX_PRICE_MONTHLY_VALUES.map((value) => {
            const label =
                value === '0'
                    ? 'No max'
                    : `${currencyFormatter.format(parseInt(value, 10))} pcm`;

            return {
                label,
                value
            };
        }),
        selectedValue: MIN_MAX_PRICE_MONTHLY_VALUES[0]
    },
    priceFrequency: {
        options: PRICE_FREQUENCY_VALUES.map((value) => {
            const label = capitalize(value.replace('per_', ''));

            return {
                label,
                value
            };
        }),
        selectedValue: ''
    },
    propertySubTypes: {
        options: PROPERTY_SUB_TYPE_VALUES.map((value) => {
            return {
                label: propertySubTypes[value],
                value
            };
        }),
        selectedValue: []
    },
    propertyAdded: {
        options: PROPERTY_ADDED_VALUES.map((value) => {
            return {
                label: propertyAdded[value],
                value
            };
        }),
        selectedValue: PROPERTY_ADDED_VALUES[0]
    },
    availableFrom: {
        options: AVAILABLE_FROM_VALUES.map((value) => {
            return {
                label: availableFrom[value],
                value
            };
        }),
        selectedValue: AVAILABLE_FROM_VALUES[0]
    },
    billsIncluded: {
        label: 'Bills included',
        selectedValue: false
    },
    underOfferOrSold: {
        label: 'Under offer / Sold STC',
        selectedValue: false
    },
    chainFree: {
        label: 'Chain-free',
        selectedValue: false
    },
    priceReduced: {
        label: 'Price-reduced',
        selectedValue: false
    },
    includeSharedAccommodation: {
        label: 'Include shared accommodation',
        selectedValue: true
    },
    includeRented: {
        label: 'Include let or let agreed',
        selectedValue: false
    },
    furnishedState: {
        options: FURNISHED_STATE_VALUES.map((value) => {
            return {
                label: furnishedState[value],
                value
            };
        }),
        selectedValue: FURNISHED_STATE_VALUES[0]
    },
    petsAllowed: {
        label: 'Pets allowed',
        selectedValue: false
    },
    rentalTerm: {
        label: 'Short lets',
        selectedValue: false
    },
    mustHaves: {
        options: MUST_HAVES_VALUES.map((value) => {
            return {
                label: featureOptions[value],
                value
            };
        }),
        selectedValue: []
    },
    isSharedOwnership: {
        label: 'Shared ownership',
        selectedValue: true,
        disabled: false
    },
    auction: {
        label: 'Auction',
        selectedValue: true,
        disabled: false
    },
    retirementHome: {
        label: 'Retirement',
        selectedValue: false
    },
    newHomes: {
        label: 'New homes',
        selectedValue: false
    },
    preOwned: {
        label: 'Pre-owned',
        selectedValue: false
    },
    incentives: {
        label: 'Help to buy',
        selectedValue: false
    },
    keywords: {
        label: 'Keywords',
        tip: 'Search for phrases by using quotations marks e.g. “double garage”, or exclude terms by prefixing them with a minus sign e.g. -studio.',
        placeholder: 'e.g. conservatory or “double garage”',
        selectedValue: ''
    },
    duration: {
        options: DURATION_VALUES.map((value) => {
            return {
                label: durationOptions[value],
                value
            };
        }),
        selectedValue: ''
    },
    transportType: {
        options: TRANSPORT_TYPE_VALUES.map((value) => {
            return {
                label: transportTypes[value],
                value
            };
        }),
        selectedValue: ''
    },
    q: {
        selectedValue: ''
    },
    identifier: {
        selectedValue: ''
    },
    locationText: {
        selectedValue: ''
    },
    locationChanged: {
        selectedValue: 'false'
    }
};
